/**
 * Device shells for the section-3 product mock-ups, drawn in CSS rather than
 * exported as images so the screens inside can be live UI.
 *
 * Every measurement is in `em`, and the stage that renders a frame sets its
 * font-size from container-query units (see `DeviceStage`). That means one
 * number — the stage's width — scales a whole device and its screen together,
 * with no ResizeObserver and no per-breakpoint sizes.
 */

import SampleDataNote from "@/components/home/mockups/sample-data-note";
import { cn } from "@/lib/utils";

type FrameProps = {
  /** Sizing and placement. Internals scale off the font-size, not this.
      Merged with `cn` so a caller's `absolute` actually beats the frame's own
      `relative` — with a plain template string both land in the stylesheet and
      Tailwind's own source order decides, which is not what the caller meant. */
  className?: string;
  children: React.ReactNode;
};

/**
 * A device's parent: declares a size container and derives the em unit from
 * its width, so `1em` is always the same fraction of the stage.
 *
 * Two elements, not one: container-query units on a container element resolve
 * against its *ancestor* container, not itself — so the `cqw` font-size has to
 * live on a child of the element that declares `container-type`, or it silently
 * falls back to viewport units.
 */
export function DeviceStage({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={className}
      // Everything a device frame holds is an invented screen — order totals,
      // branch sales, campaign reach. `data-nosnippet` is Google's opt-out for
      // exactly that, and `SampleDataNote` says the same thing to everything
      // else that reads the page. See sample-data-note.tsx.
      data-nosnippet
      style={{ containerType: "inline-size" }}
    >
      <SampleDataNote />
      <div className="relative text-[3.5cqw]">{children}</div>
    </div>
  );
}

/** iPad, landscape. */
export function IpadFrame({ className = "", children }: FrameProps) {
  return (
    <div
      className={cn(
        "relative rounded-[1.1em] bg-[#0B0B0B] p-[0.55em] shadow-[0_1.4em_2.6em_-1em_rgba(15,23,42,0.45)] ring-[0.07em] ring-[#7E9FB0]/50",
        className,
      )}
    >
      {/* Front camera, centred on the long edge like a modern iPad. */}
      <span className="absolute left-1/2 top-[0.22em] size-[0.16em] -translate-x-1/2 rounded-full bg-[#31343A]" />
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[0.62em] bg-white">
        {children}
      </div>
    </div>
  );
}

/** MacBook — lid plus the hinge bar that reads as the base. */
export function MacbookFrame({ className = "", children }: FrameProps) {
  return (
    <div className={cn("relative", className)}>
      <div className="mx-auto w-[92%] rounded-t-[0.45em] border-[0.07em] border-[#7D7D7F] bg-[#201F24] p-[0.32em] pb-[0.5em] shadow-[0_1.2em_2.4em_-1em_rgba(15,23,42,0.4)]">
        <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-[0.22em] bg-white">
          {children}
        </div>
      </div>
      <div className="relative h-[0.6em] w-full rounded-b-[0.16em] bg-[linear-gradient(180deg,#AEAFB4_0%,#AFB0B5_38%,#7D7E82_60%,#696A6F_71%,#838489_85%,#ABACB0_100%)]">
        {/* The lip you open the lid by. */}
        <span className="absolute left-1/2 top-0 h-[0.2em] w-[18%] -translate-x-1/2 rounded-b-[0.14em] bg-[linear-gradient(90deg,#25262B_0%,#D7D8DD_50%,#27282C_100%)]" />
      </div>
    </div>
  );
}

/** iPhone, portrait, with the Dynamic Island. */
export function IphoneFrame({ className = "", children }: FrameProps) {
  return (
    <div
      className={cn(
        "relative aspect-[161/327] rounded-[1.75em] border-[0.14em] border-[#CDCDCD] bg-black p-[0.3em] shadow-[0_1.4em_2.8em_-1em_rgba(15,23,42,0.5)]",
        className,
      )}
    >
      <div className="relative h-full w-full overflow-hidden rounded-[1.5em] bg-white">
        {children}
      </div>
      <span className="pointer-events-none absolute left-1/2 top-[0.62em] h-[0.42em] w-[2.4em] -translate-x-1/2 rounded-full bg-black" />
      {/* Side buttons */}
      <span className="absolute -left-[0.16em] top-[26%] h-[7%] w-[0.1em] rounded-l-[0.05em] bg-[#E9E9E9]" />
      <span className="absolute -left-[0.16em] top-[36%] h-[7%] w-[0.1em] rounded-l-[0.05em] bg-[#E9E9E9]" />
      <span className="absolute -right-[0.16em] top-[30%] h-[12%] w-[0.1em] rounded-r-[0.05em] bg-[#E9E9E9]" />
    </div>
  );
}

/**
 * iPhone 17 Pro, portrait.
 *
 * A second phone shell rather than a change to `IphoneFrame`, which the home
 * page's sections are drawn against — its 161:327 body is an older handset and
 * moving it would reflow every mock-up that already uses it.
 *
 * The geometry is the 17 Pro's own, in logical points: a 402x874 screen
 * (1206x2622 at @3x), 55pt screen corners inside a 62pt body, a ~4.4pt bezel,
 * and the 125x36.67pt Dynamic Island sitting 11pt below the top of the screen.
 * Each is written as a percentage of the device's width, so — as with the
 * frames above — the stage's font-size is the only number that scales it.
 * `1em` here is 1% of the device width, hence `text-[1cqw]` on the wrapper
 * rather than the 3.5cqw `DeviceStage` sets for the others.
 */
export function Iphone17ProFrame({ className = "", children }: FrameProps) {
  return (
    <div className={cn("text-[1cqw]", className)}>
      <div className="relative rounded-[15.4em] bg-[#1D1D1F] p-[1.6em] shadow-[0_4.9em_9.8em_-3.5em_rgba(15,23,42,0.5)]">
        <div className="relative aspect-[402/874] w-full overflow-hidden rounded-[13.7em] bg-white">
          {children}
        </div>
        {/* Dynamic Island. Measured from the device's top edge, so it carries
            the bezel as well as its own 11pt inset: 1.6 + 2.74. */}
        <span className="pointer-events-none absolute left-1/2 top-[4.34em] h-[9.1em] w-[31.1em] -translate-x-1/2 rounded-full bg-black" />
        {/* Side buttons: volume pair and the Action button on the left, the
            side button and Camera Control on the right. */}
        <span className="absolute -left-[0.5em] top-[16%] h-[3.4%] w-[0.5em] rounded-l-[0.25em] bg-[#3A3A3C]" />
        <span className="absolute -left-[0.5em] top-[23%] h-[6.2%] w-[0.5em] rounded-l-[0.25em] bg-[#3A3A3C]" />
        <span className="absolute -left-[0.5em] top-[31%] h-[6.2%] w-[0.5em] rounded-l-[0.25em] bg-[#3A3A3C]" />
        <span className="absolute -right-[0.5em] top-[24%] h-[9.5%] w-[0.5em] rounded-r-[0.25em] bg-[#3A3A3C]" />
        <span className="absolute -right-[0.5em] top-[38%] h-[5.4%] w-[0.5em] rounded-r-[0.25em] bg-[#3A3A3C]" />
      </div>
    </div>
  );
}

/**
 * The safe area an iPhone 17 Pro screen actually leaves for content: 62pt of
 * status bar and Dynamic Island at the top, 34pt of home indicator at the
 * bottom, as a fraction of the 402pt width.
 *
 * Exported so mock-up screens can pad themselves correctly instead of each
 * guessing a top inset — and so nothing draws a fake status bar into that
 * space, which is the reader's real one.
 */
export const IPHONE_17_PRO_SAFE_AREA = {
  top: "15.42%",
  bottom: "8.46%",
} as const;
